import { AxTabbedBase } from '@theme/ax-tabbed-base';

/**
 * Tabbed media blocks — Swiper-based with optional featured side panel.
 *
 * Renders media_card blocks inside per-tab carousels, optionally with a
 * featured_content block side-by-side on desktop. All tab/carousel logic lives
 * in AxTabbedBase; this subclass adds off-screen pause for autoplay videos.
 *
 * @extends {AxTabbedBase}
 */
class AxTabbedMediaBlocksComponent extends AxTabbedBase {
  static bemPrefix = 'ax-tabbed-media-blocks';

  /** @type {IntersectionObserver | null} */
  #videoVisibilityObserver = null;

  /**
   * Lifecycle — pause autoplay videos while off-screen once the base is ready.
   */
  async connectedCallback() {
    await super.connectedCallback();
    this.#setupVideoVisibility();
  }

  /**
   * Lifecycle — tear down the visibility observer.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#videoVisibilityObserver?.disconnect();
    this.#videoVisibilityObserver = null;
  }

  /**
   * Re-scan for autoplay videos after a tab is loaded/switched. The base fetches
   * other tabs' content on the first interaction, so newly injected videos must
   * be observed too.
   * @param {number} index - Target tab index
   * @param {Event} event - Originating click event
   */
  async handleTabClick(index, event) {
    await super.handleTabClick(index, event);
    this.#observeAutoplayVideos();
  }

  /**
   * Set up an IntersectionObserver that pauses autoplay background videos when
   * they leave the viewport (CPU/battery saving) and resumes them on re-entry.
   * Only muted autoplay videos are managed; manually-controlled ones are left
   * untouched.
   */
  #setupVideoVisibility() {
    this.#videoVisibilityObserver = new IntersectionObserver(this.#onVideoVisibility, { threshold: 0 });
    this.#observeAutoplayVideos();
  }

  /**
   * Observe every autoplay video in the section. IntersectionObserver.observe is
   * idempotent, so re-scanning after tab loads costs nothing for known videos.
   */
  #observeAutoplayVideos() {
    if (!this.#videoVisibilityObserver) return;

    for (const video of this.querySelectorAll('video[autoplay]')) {
      this.#videoVisibilityObserver.observe(video);
    }
  }

  /**
   * Pause off-screen videos, resume visible ones.
   * @param {IntersectionObserverEntry[]} entries - Observed visibility changes
   */
  #onVideoVisibility = (entries) => {
    for (const entry of entries) {
      const video = /** @type {HTMLVideoElement} */ (entry.target);

      if (entry.isIntersecting) {
        // Muted autoplay → play() is allowed. Ignore rejection when the source
        // is still lazy: observer-CFH will set src and autoplay once visible.
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }
  };
}

customElements.define('ax-tabbed-media-blocks-component', AxTabbedMediaBlocksComponent);
